/**
 * FILE: src/Components/Modules/LogIn/ApiClient.js
 *
 * OAuth2 redirect landing page — Microsoft sends the user here after
 * they pick their account in the MS account picker.
 *
 * FULL FLOW handled here (Steps ③ → ⑥ from diagram):
 *
 *  ③ MS redirects to /ApiClient#id_token=...&access_token=...
 *  ④ Parse id_token (JWT) → extract user's email → get domain
 *  ⑤ POST to backend Auth/SSOLogin:
 *       Backend: extracts domain → DB lookup (company SSO config)
 *       Backend: verifies domain is registered → security gate
 *       Backend: checks user exists in Employee table
 *  ⑥ Backend returns app JWT → store → navigate to dashboard
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../API_Service/AxiosConfiguration";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { setCheckResult } from "../../API_Service/HappinessIndexSlice";

// ── Status enum ───────────────────────────────────────────────────────────────
const STATUS = {
  PROCESSING: "processing", // reading token, calling backend
  SUCCESS: "success", // verified + session issued
  DOMAIN_MISMATCH: "domain_mismatch", // user's domain not registered
  NOT_REGISTERED: "not_registered", // domain ok but user not in DB
  ERROR: "error", // token parse error or network failure
};

export default function ApiClient() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState(STATUS.PROCESSING);
  const [userEmail, setUserEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    handleRedirect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRedirect() {
    // ── STEP ③: Parse the URL fragment Microsoft appended ─────────────────
    // Format: /ApiClient#id_token=xxx&access_token=yyy&token_type=Bearer...
    const hash = new URLSearchParams(window.location.hash.replace("#", ""));
    const idToken = hash.get("id_token");
    const accessToken = hash.get("access_token");
    const msError = hash.get("error");

    if (msError) {
      setStatus(STATUS.ERROR);
      setErrorMsg(
        hash.get("error_description")?.replace(/\+/g, " ") ||
          "Microsoft returned an error.",
      );
      return;
    }

    if (!idToken) {
      // No token in URL — user came here directly, not from MS redirect
      navigate("/login");
      return;
    }

    // ── STEP ④: Decode id_token → extract email ───────────────────────────
    let claims;
    try {
      claims = jwtDecode(idToken);
    } catch {
      setStatus(STATUS.ERROR);
      setErrorMsg("Failed to read identity token from Microsoft.");
      return;
    }

    // Azure AD stores email in preferred_username (most reliable), email, or upn
    const email = claims.preferred_username || claims.email || claims.upn || "";
    setUserEmail(email);

    if (!email || !email.includes("@")) {
      setStatus(STATUS.ERROR);
      setErrorMsg("Could not determine your email address from Microsoft.");
      return;
    }

    // ── STEP ⑤: Send to backend ───────────────────────────────────────────
    // Backend does:
    //   a. Extracts domain from email (e.g. "acme.com")
    //   b. DB lookup → SP_LBB_CompanyMSALConfig_GetByDomain
    //   c. If domain not registered → returns isUserExists: false (security gate)
    //   d. If registered → checks user in Employee table (SP_LBB_Login_CheckUserLogin @IsSSOLogin=1)
    //   e. Issues app JWT
    try {
      const res = await api.post("Auth/SSOLogin", {
        Email: email,
        IDToken: idToken,
        AccessToken: accessToken,
        Provider: "Microsoft",
      });

      const data = res.data;
      const user = data.user;
      if (data?.token && data?.isUserExists && data?.isRoleExists) {
        // ── STEP ⑥: Store session (same keys as normal login) ────────────
        localStorage.setItem("token", data.token);
        localStorage.setItem("companyId", data.user?.CompanyId);
        localStorage.setItem("empid", data.user?.EmployeeID);
        localStorage.setItem("empName", data.user?.EmployeeName);
        localStorage.setItem("Privillages", data.user?.UserPrivileges);
        localStorage.setItem("adminEmail", email);
        localStorage.setItem("roleId", data.user?.RoleID);
        localStorage.setItem("roleName", data.user?.RoleName);
        localStorage.setItem("roleCode", data.user?.RoleCode);
        localStorage.setItem("isFromSmile", String(data.isFromSmile ?? 0));
        localStorage.setItem("smileUrl", data.smileUrl ?? "");

        // Remove tokens from URL bar (security hygiene)
        window.history.replaceState(null, "", window.location.pathname);

        setStatus(STATUS.SUCCESS);
        setTimeout(async () => {
          if (user?.RoleCode?.toUpperCase() === "TO") {
            try {
              const checkRes = await api.get(
                `HappinessIndex/CheckToday?userId=${user.UserId}`,
              );
              const result = checkRes.data?.result ?? checkRes.data;
              dispatch(setCheckResult(result));

              if (result === "0" || result === "-1") {
                navigate("/happiness-index");
              } else {
                navigate("/lab-management/setup-lab-utilization");
              }
            } catch {
              navigate("/lab-management/setup-lab-utilization");
            }
          } else if (data.isFromSmile === 1) {
            navigate("/lab-management/lab-Booking");
          } else {
            navigate("/user-management");
          }
        }, 1000);
      } else if (data?.isUserExists === false) {
        // Domain not registered in DB — backend security gate rejected
        setStatus(STATUS.DOMAIN_MISMATCH);
      } else if (data?.isUserExists && !data?.isRoleExists) {
        // Domain valid, user found, but no role assigned
        setStatus(STATUS.NOT_REGISTERED);
      } else {
        setStatus(STATUS.NOT_REGISTERED);
      }
    } catch (err) {
      setStatus(STATUS.ERROR);
      setErrorMsg(
        err?.response?.data?.message ||
          "Backend authentication failed. Please try again.",
      );
    }
  }

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-full max-w-sm p-8 text-center">
        {status === STATUS.PROCESSING && <ProcessingState />}
        {status === STATUS.SUCCESS && <SuccessState email={userEmail} />}
        {status === STATUS.DOMAIN_MISMATCH && (
          <DomainMismatchState
            userEmail={userEmail}
            onBack={() => navigate("/login")}
          />
        )}
        {status === STATUS.NOT_REGISTERED && (
          <NotRegisteredState
            userEmail={userEmail}
            onBack={() => navigate("/login")}
          />
        )}
        {status === STATUS.ERROR && (
          <ErrorState message={errorMsg} onBack={() => navigate("/login")} />
        )}
      </div>
    </div>
  );
}

// ── Status sub-components ─────────────────────────────────────────────────────

function ProcessingState() {
  return (
    <>
      <div className="w-16 h-16 rounded-full bg-[#08757c]/10 flex items-center justify-center mx-auto mb-6">
        <span className="material-symbols-outlined text-[#08757c] text-[32px] animate-spin">
          progress_activity
        </span>
      </div>
      <h2 className="text-xl font-bold text-[#181c1d] mb-2">
        Verifying your identity…
      </h2>
      <p className="text-sm text-[#3e494a]">
        Checking your Microsoft account against company domain. Please wait.
      </p>
      {/* Step-by-step indicator */}
      <div className="mt-8 text-left space-y-3">
        {[
          "Reading Microsoft token",
          "Extracting email domain",
          "Verifying company SSO config",
          "Issuing session",
        ].map((step, i) => (
          <div
            key={i}
            className="flex items-center gap-3 text-sm text-[#3e494a]"
          >
            <span className="material-symbols-outlined text-[16px] text-[#08757c] animate-spin">
              progress_activity
            </span>
            {step}
          </div>
        ))}
      </div>
    </>
  );
}

function SuccessState({ email }) {
  return (
    <>
      <div className="w-16 h-16 rounded-full bg-[#bbf46e]/30 flex items-center justify-center mx-auto mb-6">
        <span className="material-symbols-outlined text-[#436900] text-[32px]">
          check_circle
        </span>
      </div>
      <h2 className="text-xl font-bold text-[#181c1d] mb-2">Access Granted</h2>
      <p className="text-sm text-[#3e494a]">
        Welcome, <span className="font-semibold text-[#08757c]">{email}</span>.
        <br />
        Redirecting you to the dashboard…
      </p>
    </>
  );
}

function DomainMismatchState({ userEmail, onBack }) {
  const domain = userEmail?.split("@")[1] || "";
  return (
    <>
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
        <span className="material-symbols-outlined text-[#ba1a1a] text-[32px]">
          domain_disabled
        </span>
      </div>
      <h2 className="text-xl font-bold text-[#181c1d] mb-2">Access Denied</h2>
      <p className="text-sm text-[#3e494a] mb-4 leading-relaxed">
        Your account <span className="font-semibold">{userEmail}</span> belongs
        to domain{" "}
        <span className="font-semibold text-[#ba1a1a]">@{domain}</span>, which
        is not registered with this application.
      </p>
      <p className="text-xs text-[#3e494a] mb-6">
        Please sign in with your company-issued Microsoft account, or contact
        your Administrator to register your company domain.
      </p>
      <button
        onClick={onBack}
        className="w-full py-3 rounded-xl bg-[#08757c] text-white text-sm font-semibold hover:bg-[#005b60] transition-colors"
      >
        Back to Login
      </button>
    </>
  );
}

function NotRegisteredState({ userEmail, onBack }) {
  return (
    <>
      <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
        <span className="material-symbols-outlined text-amber-600 text-[32px]">
          person_off
        </span>
      </div>
      <h2 className="text-xl font-bold text-[#181c1d] mb-2">
        Account Not Found
      </h2>
      <p className="text-sm text-[#3e494a] mb-4 leading-relaxed">
        Your account{" "}
        <span className="font-semibold text-[#08757c]">{userEmail}</span> is
        from the correct company domain, but hasn't been added to this
        application yet.
      </p>
      <p className="text-xs text-[#3e494a] mb-6">
        Please contact your Administrator to add your account before signing in.
      </p>
      <button
        onClick={onBack}
        className="w-full py-3 rounded-xl bg-[#08757c] text-white text-sm font-semibold hover:bg-[#005b60] transition-colors"
      >
        Back to Login
      </button>
    </>
  );
}

function ErrorState({ message, onBack }) {
  return (
    <>
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
        <span className="material-symbols-outlined text-[#ba1a1a] text-[32px]">
          error
        </span>
      </div>
      <h2 className="text-xl font-bold text-[#181c1d] mb-2">
        Authentication Error
      </h2>
      <p className="text-sm text-[#3e494a] mb-6 leading-relaxed">
        {message || "Something went wrong during Microsoft sign-in."}
      </p>
      <button
        onClick={onBack}
        className="w-full py-3 rounded-xl bg-[#08757c] text-white text-sm font-semibold hover:bg-[#005b60] transition-colors"
      >
        Back to Login
      </button>
    </>
  );
}
